function __ta(p){try{return (window.TOPIA_ASSETS&&window.TOPIA_ASSETS[p])||p;}catch(e){return p;}}
const root = document.documentElement;
const canvas = document.querySelector("#particle-field");
const ctx = canvas.getContext("2d");
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

document.body.classList.add("motion-ready");

function updateZoomGuard() {
  const dpr = window.devicePixelRatio || 1;
  // Skip on real mobile/tablet — iPhones/iPads have dpr 2-3 natively and would
  // always trigger the desktop-zoom heuristic, breaking the mobile grid stack.
  const isTouchMobile =
    window.matchMedia("(hover: none) and (pointer: coarse)").matches ||
    window.matchMedia("(max-width: 980px)").matches;
  if (isTouchMobile) {
    document.body.classList.remove("zoom-guard");
    return;
  }
  const likelyZoomedRetina = dpr >= 2.35 && window.innerWidth <= 1400;
  const likelyZoomedStandard = dpr >= 1.35 && dpr < 2 && window.innerWidth <= 1250;
  document.body.classList.toggle("zoom-guard", likelyZoomedRetina || likelyZoomedStandard);
}

updateZoomGuard();
window.addEventListener("resize", updateZoomGuard);

let width = 0;
let height = 0;
let particles = [];

function resizeCanvas() {
  const ratio = Math.min(window.devicePixelRatio || 1, 2);
  width = window.innerWidth;
  height = window.innerHeight;
  canvas.width = Math.floor(width * ratio);
  canvas.height = Math.floor(height * ratio);
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  const count = Math.min(110, Math.max(52, Math.floor(width / 16)));
  particles = Array.from({ length: count }, (_, index) => ({
    x: Math.random() * width,
    y: Math.random() * height,
    vx: (Math.random() - 0.5) * 0.36,
    vy: (Math.random() - 0.5) * 0.32,
    r: Math.random() * 1.7 + 0.4,
    hue: index % 5 === 0 ? 166 : index % 3 === 0 ? 284 : 198
  }));
}

function drawParticles() {
  ctx.clearRect(0, 0, width, height);
  for (const p of particles) {
    p.x += p.vx;
    p.y += p.vy;
    if (p.x < -20) p.x = width + 20;
    if (p.x > width + 20) p.x = -20;
    if (p.y < -20) p.y = height + 20;
    if (p.y > height + 20) p.y = -20;

    ctx.beginPath();
    ctx.fillStyle = `hsla(${p.hue}, 100%, 65%, 0.78)`;
    ctx.shadowBlur = 14;
    ctx.shadowColor = `hsla(${p.hue}, 100%, 62%, 0.9)`;
    ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.shadowBlur = 0;
  for (let i = 0; i < particles.length; i += 1) {
    for (let j = i + 1; j < particles.length; j += 1) {
      const a = particles[i];
      const b = particles[j];
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      const distance = Math.hypot(dx, dy);
      if (distance < 128) {
        ctx.strokeStyle = `rgba(92, 188, 255, ${0.13 * (1 - distance / 128)})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
    }
  }

  if (!reducedMotion) requestAnimationFrame(drawParticles);
}

window.addEventListener("resize", resizeCanvas);
resizeCanvas();
drawParticles();

window.addEventListener("pointermove", (event) => {
  root.style.setProperty("--mx", `${event.clientX}px`);
  root.style.setProperty("--my", `${event.clientY}px`);
});

const revealObserver = new IntersectionObserver((entries) => {
  for (const entry of entries) {
    if (entry.isIntersecting) {
      entry.target.classList.add("is-visible");
      revealObserver.unobserve(entry.target);
    }
  }
}, { threshold: 0.15 });

document.querySelectorAll("[data-reveal]").forEach((item) => revealObserver.observe(item));

const counterObserver = new IntersectionObserver((entries) => {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;
    const node = entry.target;
    const target = Number(node.dataset.count || 0);
    const start = performance.now();
    const duration = 1100;
    function step(now) {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      node.textContent = `${Math.round(target * eased)}+`;
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
    counterObserver.unobserve(node);
  }
}, { threshold: 0.8 });

document.querySelectorAll("[data-count]").forEach((item) => counterObserver.observe(item));

const modeData = {
  personal: {
    kicker: "Personal AI setup for professionals, creators, founders, and freelancers.",
    marquee: ["Claude", "ChatGPT", "Gemini", "Midjourney", "Cursor", "Sora", "Runway", "ElevenLabs", "OpenClaw", "Hermes"],
    hero: {
      eyebrow: "Personal AI setup plus a living AI library",
      title: "AI installed into your machine, your workflow, and your week.",
      copy: "Topia Labs helps you stop guessing which AI tools matter. We configure your stack, walk you through the workflows, and keep you current as AI changes every week.",
      image: "assets/topia-personal-offer-v3-sq.png",
      alt: "Topia Labs personal AI setup visual",
      panelA: ["Setup running", "Machine -> Tools -> Walkthroughs"],
      panelB: ["Signal", "48 tools filtered to what you need"],
      metrics: [["48+", "Curated AI tools"], ["8+", "Real workflows"], ["Weekly", "AI updates"]]
    },
    consulting: {
      eyebrow: "Personal AI works",
      title: "Everything you need to make AI useful on your own machine.",
      copy: "Start with a free 30-minute discovery call. Then choose the level of guidance you want: async walkthroughs, live strategy, or hands-on weekly coaching.",
      offerEyebrow: "Personal AI setup",
      offerTitle: "AI installed on YOUR machine.",
      offerCopy: "Skip the course maze. We help configure the tools you actually need, record walkthroughs you keep forever, and support you month to month.",
      offerList: [
        "Core: $99/mo for a custom Loom plus email Q&A",
        "Advanced: $299/mo with monthly call, Loom, and Slack-style support",
        "Elite: $999/mo with weekly calls and unlimited Loom requests"
      ]
    },
    tools: {
      eyebrow: "Personal tool stack",
      title: "Know which AI tools actually matter.",
      copy: "Clear tutorials for the tools professionals are using right now, sorted by outcome instead of hype.",
      categories: [
        ["Chat", "Writing, brainstorming, support, and reasoning."],
        ["Research", "Find, summarize, and analyze information faster."],
        ["Coding", "Build, debug, and ship with AI-powered code."],
        ["Image", "Generate, edit, and enhance brand visuals."],
        ["Video", "Create, edit, and transform short-form content."],
        ["Audio", "Transcribe, generate, and enhance voice work."],
        ["Agents", "Autonomous AI systems that get work done."],
        ["Automation", "Connect tools and reduce repeat work."]
      ]
    },
    system: {
      eyebrow: "Personal AI operating system",
      title: "Your stack should feel like an advantage, not homework.",
      copy: "We help you install and organize the tools around the way you already work: writing, research, coding, content, automation, and weekly updates.",
      image: "assets/topia-personal-stack-v3-sq.png",
      cards: [
        ["Configured Stack", "The right tools installed, organized, and ready to use."],
        ["Guided Walkthroughs", "Custom Looms and live support around your actual workflow."],
        ["Weekly Updates", "Stay current as models, tools, and tactics change."]
      ]
    },
    pricing: {
      eyebrow: "Simple membership. Premium support.",
      title: "Pick the lane that matches how much help you want.",
      cards: [
        ["Membership", "Monthly", "$19<small>/mo</small>", "Explore the AI tools library, workflows, and weekly updates.", "Start Monthly"],
        ["Most popular", "Personal Advanced", "$299<small>/mo</small>", "Monthly strategy call, custom Loom, AI stack guidance, and Q&A support.", "Book Discovery"],
        ["White glove", "Personal Elite", "$999<small>/mo</small>", "Weekly calls, unlimited Loom requests, and priority support.", "Go Elite"]
      ]
    },
    workflows: {
      eyebrow: "Real personal workflows",
      title: "AI that does actual work this week.",
      copy: "Recipes and playbooks built for writing, research, content, coding, job performance, and everyday productivity.",
      items: [
        ["Research", "From idea to defensible point of view.", "Use AI search, synthesis, and structured prompts to map a market, identify competitors, summarize customer pain, and leave with a sharper strategy in an afternoon."],
        ["Content ops", "Build a personal brand without faking it.", "Turn your real thinking into posts, outlines, and repurposed content while keeping your voice intact. Thirty minutes a week, no generic corporate filler."],
        ["Career leverage", "Use AI as your always-on workbench.", "Summarize meetings, draft follow-ups, prepare research, and build reusable prompts for the work you repeat every week."],
        ["Knowledge", "Turn recordings into searchable notes.", "Convert calls, screenshots, and rough ideas into organized documents you can reuse instead of starting over."]
      ]
    },
    cta: {
      eyebrow: "Start with a free 30-minute call",
      title: "Get AI working on your terms.",
      copy: "Tell Topia Labs what you do, what feels slow, and where you want leverage. We will recommend the right personal setup path."
    }
  },
  business: {
    kicker: "Business AI consulting for teams that want strategy, rollout, training, and measurable ROI.",
    marquee: ["Workflow Audit", "Tool Selection", "Rollout Plan", "Team Training", "ROI Roadmap", "Automation", "Knowledge Systems", "Support Ops", "OpenClaw", "Hermes"],
    hero: {
      eyebrow: "Business AI consulting without guessing",
      title: "Bring AI into your business with a real rollout plan.",
      copy: "Topia Labs audits your workflows, finds the highest-leverage AI opportunities, selects the right tools, trains your team, and helps you turn experiments into operating systems.",
      image: "assets/topia-business-offer-v3-sq.png",
      alt: "Topia Labs business AI consulting visual",
      panelA: ["Engagement running", "Audit -> Roadmap -> Rollout"],
      panelB: ["Outcome", "AI tied to hours saved and revenue lifted"],
      metrics: [["Audit", "From $4,997"], ["12 weeks", "Advanced rollout"], ["Retainer", "Ongoing AI lead"]]
    },
    consulting: {
      eyebrow: "Business AI works",
      title: "A custom AI strategy for your business, not a template.",
      copy: "Every engagement starts with a free 30-minute discovery call. Then we map the right tier based on your team, bottlenecks, tools, and timeline.",
      offerEyebrow: "Business AI consulting",
      offerTitle: "AI adoption built around your business reality.",
      offerCopy: "We audit operations, identify high-ROI use cases, build implementation playbooks, train the team, and stay close enough to make the change stick.",
      offerList: [
        "Core: $4,997 AI audit, roadmap, and first implementation playbook",
        "Advanced: $14,997 12-week engagement with calls, vendor selection, and training",
        "Elite: $7,500/mo fractional AI lead for ongoing ownership and optimization"
      ]
    },
    tools: {
      eyebrow: "Business capability map",
      title: "AI systems for real business impact.",
      copy: "The focus shifts from tools to outcomes: where AI saves time, improves quality, increases throughput, or creates new capacity.",
      categories: [
        ["Workflow Audit", "Find bottlenecks, repeated tasks, and high-ROI opportunities."],
        ["Tool Selection", "Choose the right models, apps, agents, and vendors."],
        ["Rollout Plan", "Turn recommendations into a practical 30-60-90 day plan."],
        ["Team Training", "Upskill the people who will use the system every week."],
        ["Operations", "Automate internal process, reporting, and repetitive admin."],
        ["Marketing", "Build content, campaign, and creative production workflows."],
        ["Support", "Improve customer response, triage, and knowledge retrieval."],
        ["Governance", "Keep usage secure, auditable, and aligned with business goals."]
      ]
    },
    system: {
      eyebrow: "Business AI operating system",
      title: "From scattered AI experiments to coordinated execution.",
      copy: "Agents and automation become useful when they connect to real business processes. We help teams build systems that remember context, route work, report progress, and stay accountable.",
      image: "assets/topia-business-stack-v3-sq.png",
      cards: [
        ["Strategy", "A roadmap tied to cost saved, revenue lifted, and hours returned."],
        ["Implementation", "Playbooks, vendor selection, training, and rollout support."],
        ["Optimization", "Ongoing refinement so the stack improves as the company grows."]
      ]
    },
    pricing: {
      eyebrow: "Business consulting tiers",
      title: "Pick the depth of transformation your team needs.",
      cards: [
        ["Core", "AI Audit", "$4,997<small> one-time</small>", "Workflow audit, roadmap, and first implementation playbook.", "Start Core"],
        ["Most popular", "Advanced Rollout", "$14,997<small> / 12 weeks</small>", "Audit, weekly strategy calls, vendor selection, playbooks, and team training.", "Book Advanced"],
        ["Executive", "Fractional AI Lead", "$7,500<small>/mo</small>", "Ongoing AI leadership, office hours, vendor management, and executive reviews.", "Inquire Elite"]
      ]
    },
    workflows: {
      eyebrow: "Business workflows",
      title: "AI mapped to the work that actually moves the business.",
      copy: "Business mode focuses on measurable leverage across operations, marketing, customer support, research, reporting, and knowledge systems.",
      items: [
        ["Operations", "Automate the work slowing your team down.", "Map repetitive internal tasks, identify where AI can safely assist, and design a rollout that reduces manual drag without confusing the team."],
        ["Customer support", "Triage and draft responses before the queue backs up.", "Use AI to classify inbound requests, surface relevant knowledge, draft first responses, and escalate the cases that need human judgment."],
        ["Marketing", "Build a campaign production engine.", "Create repeatable workflows for research, briefs, creative variations, publishing calendars, and performance summaries."],
        ["Knowledge systems", "Turn team expertise into searchable operating memory.", "Convert calls, SOPs, training notes, and decision logs into a structured knowledge base your team can retrieve from."]
      ]
    },
    cta: {
      eyebrow: "Start with a free business discovery call",
      title: "Modernize your business with AI - the right way.",
      copy: "Tell Topia Labs what your business does, where work feels slow, and what outcomes matter. We will recommend the right engagement level."
    }
  }
};

const qs = (selector) => document.querySelector(selector);
const qsa = (selector) => [...document.querySelectorAll(selector)];

function setText(selector, value) {
  const node = qs(selector);
  if (node) node.textContent = value;
}

function renderList(node, items) {
  node.innerHTML = items.map((item) => `<li>${item}</li>`).join("");
}

function applyMode(mode) {
  const data = modeData[mode];
  document.body.dataset.mode = mode;

  qsa("[data-mode-target]").forEach((button) => {
    const active = button.dataset.modeTarget === mode;
    button.classList.toggle("active", active);
    button.setAttribute("aria-selected", String(active));
  });

  setText("#mode-kicker", data.kicker);
  qs(".marquee-track").innerHTML = [...data.marquee, ...data.marquee].map((item) => `<span>${item}</span>`).join("");

  setText("#hero-eyebrow", data.hero.eyebrow);
  setText("#hero-title", data.hero.title);
  setText("#hero-copy", data.hero.copy);
  const heroEl = qs("#hero-image");
  if (heroEl && heroEl.tagName === "IMG") {
    heroEl.src = __ta(data.hero.image);
    heroEl.alt = data.hero.alt;
  }
  // hero element is a <video> in this build — skip src/alt swap, the video is mode-agnostic
  setText("#panel-a-label", data.hero.panelA[0]);
  setText("#panel-a-copy", data.hero.panelA[1]);
  setText("#panel-b-label", data.hero.panelB[0]);
  setText("#panel-b-copy", data.hero.panelB[1]);
  qsa(".metric-strip article").forEach((card, index) => {
    card.querySelector("strong").textContent = data.hero.metrics[index][0];
    card.querySelector("span").textContent = data.hero.metrics[index][1];
  });

  setText("#consulting-eyebrow", data.consulting.eyebrow);
  setText("#consulting-title", data.consulting.title);
  setText("#consulting-copy", data.consulting.copy);
  setText("#offer-eyebrow", data.consulting.offerEyebrow);
  setText("#offer-title", data.consulting.offerTitle);
  setText("#offer-copy", data.consulting.offerCopy);
  renderList(qs("#offer-list"), data.consulting.offerList);
  qs("#offer-image").src = __ta(data.hero.image);
  qs("#offer-image").alt = data.hero.alt;

  setText("#tools-eyebrow", data.tools.eyebrow);
  setText("#tools-title", data.tools.title);
  setText("#tools-copy", data.tools.copy);
  qsa(".category-card").forEach((card, index) => {
    const item = data.tools.categories[index];
    card.querySelector("h3").textContent = item[0];
    card.querySelector("p").textContent = item[1];
  });

  setText("#system-eyebrow", data.system.eyebrow);
  setText("#system-title", data.system.title);
  setText("#system-copy", data.system.copy);
  qs("#system-image").src = __ta(data.system.image);
  qs("#system-image").alt = data.hero.alt;
  qsa(".mini-grid article").forEach((card, index) => {
    const item = data.system.cards[index];
    card.querySelector("h3").textContent = item[0];
    card.querySelector("p").textContent = item[1];
  });

  setText("#pricing-eyebrow", data.pricing.eyebrow);
  setText("#pricing-title", data.pricing.title);
  qsa(".price-card").forEach((card, index) => {
    const item = data.pricing.cards[index];
    card.querySelector("span").textContent = item[0];
    card.querySelector("h3").textContent = item[1];
    card.querySelector(".price").innerHTML = item[2];
    card.querySelector("p").textContent = item[3];
    card.querySelector(".mini-btn").textContent = item[4];
  });

  setText("#workflow-eyebrow", data.workflows.eyebrow);
  setText("#workflow-section-title", data.workflows.title);
  setText("#workflow-section-copy", data.workflows.copy);
  qsa(".workflow-item").forEach((button, index) => {
    button.textContent = data.workflows.items[index][1];
    button.dataset.workflowIndex = String(index);
    button.classList.toggle("active", index === 0);
  });
  renderWorkflow(mode, 0);

  setText("#cta-eyebrow", data.cta.eyebrow);
  setText("#cta-title", data.cta.title);
  setText("#cta-copy", data.cta.copy);
}

function renderWorkflow(mode, index) {
  const item = modeData[mode].workflows.items[index];
  qs("#workflow-label").textContent = item[0];
  qs("#workflow-title").textContent = item[1];
  qs("#workflow-copy").textContent = item[2];
}

qsa("[data-mode-target]").forEach((button) => {
  button.addEventListener("click", () => applyMode(button.dataset.modeTarget));
});

document.querySelectorAll(".glass").forEach((card) => {
  card.addEventListener("pointermove", (event) => {
    const box = card.getBoundingClientRect();
    card.style.setProperty("--px", `${event.clientX - box.left}px`);
    card.style.setProperty("--py", `${event.clientY - box.top}px`);
  });
});

document.querySelectorAll("[data-tilt]").forEach((card) => {
  card.addEventListener("pointermove", (event) => {
    const box = card.getBoundingClientRect();
    const x = (event.clientX - box.left) / box.width - 0.5;
    const y = (event.clientY - box.top) / box.height - 0.5;
    card.style.setProperty("--rx", `${(-y * 6).toFixed(2)}deg`);
    card.style.setProperty("--ry", `${(x * 8).toFixed(2)}deg`);
  });
  card.addEventListener("pointerleave", () => {
    card.style.setProperty("--rx", "0deg");
    card.style.setProperty("--ry", "0deg");
  });
});

const workflowButtons = document.querySelectorAll("[data-workflow]");

workflowButtons.forEach((button) => {
  button.addEventListener("click", () => {
    workflowButtons.forEach((item) => item.classList.toggle("active", item === button));
    renderWorkflow(document.body.dataset.mode, Number(button.dataset.workflowIndex || 0));
  });
});

applyMode("personal");

document.querySelectorAll('a[href^="#"]').forEach((link) => {
  link.addEventListener("click", (event) => {
    const target = document.querySelector(link.getAttribute("href"));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
  });
});
